import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import FluidMask from '@/components/base/fluid-mask'
import Grid from '@/components/base/grid'
import Media, { MediaElement } from '@/components/base/media'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import TextEffect from '@/components/base/text-effect'
import Typography from '@/components/base/typography'

import { imageTextRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type ImageTextProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    media: MediaElement
    title: string
    subtitle: string
    paragraph_1: string
    paragraph_2: string
    double_paragraph: boolean
    alignment: 'left' | 'right'
    theme: 'dark' | 'light'
    button: WPLink
    text_color: string
  }>

const ImageText = ({
  media,
  title,
  subtitle,
  paragraph_1,
  paragraph_2,
  button,
  double_paragraph,
  alignment,
  className,
  theme,
  text_color,
  ...props
}: ImageTextProps) => {
  return (
    <Motion animation={imageTextRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} className={cn(className)} {...props}>
          <Container>
            <Grid className="items-center gap-y-spacing-lg">
              <div
                className={cn(
                  'col-span-2 md:col-span-3 lg:col-span-6',
                  {
                    'lg:col-start-2': alignment === 'left',
                    'md:order-2 lg:col-start-7': alignment === 'right'
                  },
                  'grid grid-cols-2 gap-x-gutter gap-y-spacing-sm',
                  {
                    'lg:gap-y-spacing-lg xl:gap-y-spacing-2xl': double_paragraph
                  }
                )}
              >
                <div
                  className={cn(
                    'col-span-2',
                    {
                      'lg:col-span-1': double_paragraph
                    },
                    'grid gap-y-spacing-lg'
                  )}
                >
                  {title ? (
                    <Typography
                      variant="h4"
                      component="h2"
                      data-motion-title
                      {...(text_color ? { style: { color: text_color } } : {})}
                    >
                      {title}
                    </Typography>
                  ) : null}

                  {subtitle ? (
                    <Typography
                      variant="large"
                      paragraph
                      data-motion-subtitle
                      {...(text_color ? { style: { color: text_color } } : {})}
                    >
                      {subtitle}
                    </Typography>
                  ) : null}
                </div>

                <div
                  className={cn(
                    'col-span-2',
                    'grid grid-cols-2 gap-x-gutter gap-y-spacing-lg md:gap-y-spacing-sm'
                  )}
                >
                  {paragraph_1 ? (
                    <div
                      className={cn('col-span-2', {
                        'lg:col-span-1': double_paragraph
                      })}
                    >
                      <Typography
                        variant="regular"
                        className="block"
                        data-motion-paragraph1
                        {...(text_color
                          ? { style: { color: text_color } }
                          : {})}
                      >
                        {paragraph_1}
                      </Typography>
                    </div>
                  ) : null}

                  {paragraph_2 ? (
                    <div
                      className={cn('col-span-2', {
                        'lg:col-span-1': double_paragraph
                      })}
                    >
                      <Typography
                        variant="regular"
                        className="block"
                        data-motion-paragraph2
                        {...(text_color
                          ? { style: { color: text_color } }
                          : {})}
                      >
                        {paragraph_2}
                      </Typography>
                    </div>
                  ) : null}

                  {button && button.url ? (
                    <div className="col-span-2">
                      <Button
                        isLiquid
                        asChild
                        variant="primary"
                        isInverted={theme === 'dark'}
                        data-motion-button
                      >
                        <Link
                          href={button.url}
                          target={button.target}
                          scroll={false}
                        >
                          <TextEffect>
                            <Typography variant="regular">
                              {button.title}
                            </Typography>
                          </TextEffect>
                        </Link>
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div
                className={cn(
                  'col-span-2 md:col-span-3 lg:col-span-5 xl:col-span-4',
                  {
                    'lg:col-start-8 xl:col-start-9': alignment === 'left',
                    'md:order-1 lg:col-start-2 xl:col-start-2':
                      alignment === 'right'
                  }
                )}
              >
                <div className="relative aspect-square" data-motion-media>
                  <FluidMask amplitude={10}>
                    {(ref) =>
                      button && button.url ? (
                        <Link
                          href={button.url}
                          target={button.target}
                          scroll={false}
                          className="absolute inset-0 z-10 block h-full w-full"
                        >
                          <Media ref={ref} fill {...media} />
                        </Link>
                      ) : (
                        <Media ref={ref} fill {...media} />
                      )
                    }
                  </FluidMask>
                </div>
              </div>
            </Grid>
          </Container>
        </Section>
      )}
    </Motion>
  )
}

export default ImageText
